import InteractionPlugin from "@components/Map/plugins/interaction-control";
import { PLUGIN_IDS } from "@components/Map/plugins/types";
import LivingMap from "@livingmap/core-mapping";
import { useAppDispatch } from "@redux/hooks";
import {
  useSaveFeatureForMapByIdMutation,
  useDeleteFeatureForMapByIdMutation,
  useUnarchiveFeatureForMapByIdMutation,
  useRevertFeatureChangesForMapByIdMutation,
  useGetAvailableCategoriesForFeatureQuery,
  useGetAvailableIconsForFeatureQuery,
  useGetFeatureForMapByIdQuery,
} from "@redux/services/mms";
import {
  setFeatureID,
  setHasUnsavedChanges,
} from "@redux/slices/applicationSlice";
import { displayToast } from "@utils";
import { GridApi } from "ag-grid-community";
import { t } from "i18next";
import { useCallback, useEffect } from "react";

interface UseSideSheetPropsParams {
  mapID: string | null;
  featureID: string | null;
  mapInstance: LivingMap | null;
  gridApi?: GridApi | null;
}

const useSideSheet = ({
  mapID,
  featureID,
  mapInstance,
  gridApi,
}: UseSideSheetPropsParams) => {
  const dispatch = useAppDispatch();

  const {
    data: featureData,
    isFetching: isFetchingSingleFeature,
    error: featureDataError,
  } = useGetFeatureForMapByIdQuery(
    {
      mapID,
      featureID,
    },
    { skip: !featureID },
  );

  const { data: categoryData } = useGetAvailableCategoriesForFeatureQuery(
    {
      mapID,
      featureID,
    },
    { skip: !featureID },
  );

  const { data: iconData } = useGetAvailableIconsForFeatureQuery(
    {
      mapID,
      featureID,
    },
    { skip: !featureID },
  );

  const [updateFeature, updateFeatureResponse] =
    useSaveFeatureForMapByIdMutation();
  const [deleteFeature, deleteFeatureResponse] =
    useDeleteFeatureForMapByIdMutation();
  const [unarchiveFeature, unarchiveFeatureResponse] =
    useUnarchiveFeatureForMapByIdMutation();
  const [revertFeatureChanges, revertFeatureChangesResponse] =
    useRevertFeatureChangesForMapByIdMutation();

  const isLoading =
    updateFeatureResponse.status === "pending" ||
    revertFeatureChangesResponse.status === "pending" ||
    deleteFeatureResponse.status === "pending" ||
    unarchiveFeatureResponse.status === "pending";

  const handleSidebarClose = () => {
    dispatch(setFeatureID(null));
    dispatch(setHasUnsavedChanges(false));
    setTimeout(() => {
      gridApi?.sizeColumnsToFit();
      mapInstance?.getMapboxMap().resize();
    });
  };

  // useEffect to refresh all mapbox tiles once the data has updated & been refetched
  useEffect(() => {
    if (!isLoading) {
      const source = mapInstance?.getMapboxMap().getSource("lm");
      const interactionControlInstance =
        mapInstance?.getPluginById<InteractionPlugin>(PLUGIN_IDS.INTERACTION);

      if (source && interactionControlInstance) {
        interactionControlInstance.deselectFeatures();
        // @ts-ignore: types not available for the reload call
        source.reload();
      }
    }
  }, [isLoading, mapInstance]);

  useEffect(() => {
    if (
      featureDataError &&
      "status" in featureDataError &&
      featureDataError.status === 404
    ) {
      displayToast("error", t("views.map_view.errors.not_editable"), {
        position: "top-center",
      });
    }
  }, [featureDataError]);

  // Set whether there are any unsaved changes in the redux store, so we can display warning messages throughout the app when navigating away from unsaved changes
  const handleDataChanged = useCallback(
    (dataChanged: boolean) => {
      dispatch(setHasUnsavedChanges(dataChanged));
    },
    [dispatch],
  );

  return {
    isLoading,
    isFetchingSingleFeature,
    featureData,
    categoryData,
    iconData,
    updateFeature,
    deleteFeature,
    unarchiveFeature,
    revertFeatureChanges,
    handleDataChanged,
    handleSidebarClose,
  };
};

export default useSideSheet;
